import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "../App";

// Trace a hashboard serial (e.g. from a repair invoice) back to its machine.
// "Seen in" is a board we actually recorded in that machine; "Likely" is a
// guess from serial similarity, for boards read before we kept a history.
const BoardLookup = ({ customersOnly = false }) => {
  const [sn, setSn] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  // Re-run when the My Customers / All switch changes, so results match it.
  useEffect(() => {
    if (res && !res.error) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersOnly]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = async () => {
    const q = sn.trim();
    if (q.length < 6) return;
    setLoading(true);
    setOpen(true);
    try {
      const r = await axios.get(`${API}/boards/lookup`, { params: { sn: q, customers: customersOnly } });
      setRes(r.data);
    } catch {
      setRes({ error: true });
    }
    setLoading(false);
  };

  const Row = ({ m, tag, tagClass, note }) => (
    <div className="px-3 py-2 border-t border-[#27272A] first:border-t-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tagClass}`}>{tag}</span>
        <span className="text-white text-sm font-medium">{m.worker_now || m.worker_name || "—"}</span>
        {m.ip_now && <span className="text-gray-400 font-mono text-xs">{m.ip_now}</span>}
        {m.status_now && (
          <span className={`text-xs ${m.status_now === "online" ? "text-[#00E054]" : "text-red-400"}`}>{m.status_now}</span>
        )}
        {m.in_repair && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/40">in repair</span>
        )}
        {m.boards_expected > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40"
                title="Running on fewer hashboards than it should -- the board may be the one missing">
            {m.boards_ok}/{m.boards_expected} boards
          </span>
        )}
      </div>
      <div className="text-[11px] text-gray-500 mt-0.5 font-mono">
        {m.model ? `${m.model} · ` : ""}{m.machine_sn || "machine SN unknown"}
      </div>
      {note && <div className="text-[11px] text-gray-500 mt-0.5">{note}</div>}
    </div>
  );

  return (
    <div className="relative" ref={box}>
      <input
        placeholder="Board / PSU SN…"
        value={sn}
        onChange={(e) => setSn(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && search()}
        onFocus={() => res && setOpen(true)}
        className="bg-[#1A1A1A] border border-[#27272A] rounded-md text-sm w-full sm:w-44 h-9 px-3 text-white outline-none focus:border-cyan-500 font-mono"
        data-testid="board-lookup"
        title="Paste a hashboard, PSU or machine serial from a repair invoice and press Enter"
      />
      {open && (
        <div className="absolute right-0 mt-1 w-[22rem] max-w-[90vw] bg-[#0F0F0F] border border-[#27272A] rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
          {loading ? (
            <p className="px-3 py-3 text-sm text-gray-500">Searching…</p>
          ) : res?.error ? (
            <p className="px-3 py-3 text-sm text-red-400">Lookup failed</p>
          ) : res && !res.exact?.length && !res.suggestions?.length ? (
            <p className="px-3 py-3 text-sm text-gray-500">
              No machine matches {res.query}. Board serials are recorded once a day per machine, so a board removed before that started can only be matched by similarity.
            </p>
          ) : res ? (
            <>
              {res.exact.map((m, i) => (
                <Row key={`e-${i}`} m={m}
                     tag={m.kind === "psu" ? "PSU of" : m.kind === "machine" ? "Machine" : "Seen in"}
                     tagClass="border-[#00E054]/40 text-[#00E054] bg-[#00E054]/10"
                     note={m.kind === "psu" ? "PSU serial matches this machine's power supply"
                           : m.kind === "machine" ? "This is the machine's own serial"
                           : `Board ${m.sn} · last seen ${new Date(m.last_seen).toLocaleDateString()}`} />
              ))}
              {res.suggestions.map((m, i) => (
                <Row key={`s-${i}`} m={m} tag="Likely" tagClass="border-amber-500/40 text-amber-300 bg-amber-500/10" note={m.why} />
              ))}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default BoardLookup;
